import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orgApi } from '../services/api';
import { Sparkles, ArrowRight, X } from 'lucide-react';

/**
 * Compact plan-usage banner. Shows on Dashboard for non-enterprise plans.
 *  - Hidden entirely for enterprise (legacy default org)
 *  - Subtle when usage <80% on every resource
 *  - Highlighted (amber) when any resource is between 80-99%
 *  - Loud (rose) when any resource is at limit
 *
 * Lives in localStorage so the user can dismiss it for the current session.
 */
const RESOURCES = [
  { key: 'users', label: 'Users' },
  { key: 'jobs', label: 'Jobs' },
  { key: 'candidates', label: 'Candidates' },
  { key: 'clients', label: 'Clients' },
];

const PlanUsageBanner = () => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('cc_plan_banner_dismissed') === '1'
  );

  useEffect(() => {
    let alive = true;
    orgApi
      .getUsage()
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!data || dismissed) return null;
  if (data.plan === 'enterprise') return null;

  const usage = data.usage || {};
  const resources = RESOURCES.map((r) => ({
    ...r,
    ...(usage[r.key] || { used: 0, limit: null, pct: 0, at_limit: false }),
  }));
  const anyAtLimit = resources.some((r) => r.at_limit);
  const anyWarn = resources.some((r) => r.pct >= 80 && !r.at_limit);

  const tone = anyAtLimit
    ? {
        wrap: 'border-rose-300 bg-rose-50',
        accent: 'text-rose-700',
        bar: 'bg-rose-500',
        track: 'bg-rose-100',
      }
    : anyWarn
    ? {
        wrap: 'border-amber-300 bg-amber-50',
        accent: 'text-amber-800',
        bar: 'bg-amber-500',
        track: 'bg-amber-100',
      }
    : {
        wrap: 'border-slate-200 bg-slate-50/60',
        accent: 'text-teal-700',
        bar: 'bg-teal-500',
        track: 'bg-slate-200',
      };

  return (
    <div
      className={`rounded-xl border ${tone.wrap} px-4 py-3`}
      data-testid="plan-usage-banner"
    >
      <div className="flex items-start gap-3">
        <div className="hidden sm:flex w-9 h-9 rounded-lg bg-white border border-slate-200 items-center justify-center flex-shrink-0">
          <Sparkles className={`w-4 h-4 ${tone.accent}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-sm font-semibold text-slate-900">
              {anyAtLimit ? (
                <>You've hit a {data.plan_label} plan limit.</>
              ) : anyWarn ? (
                <>You're approaching your {data.plan_label} plan limits.</>
              ) : (
                <>You're on the {data.plan_label} plan.</>
              )}
              {data.next_plan_label ? (
                <span className="text-slate-500 font-normal">
                  {' '}
                  Upgrade to <span className={`font-semibold ${tone.accent}`}>
                    {data.next_plan_label}
                  </span>{' '}
                  for higher limits.
                </span>
              ) : null}
            </p>
            <button
              onClick={() => {
                localStorage.setItem('cc_plan_banner_dismissed', '1');
                setDismissed(true);
              }}
              className="text-slate-400 hover:text-slate-700"
              data-testid="plan-banner-dismiss"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {resources.map((r) => (
              <div key={r.key} data-testid={`plan-usage-${r.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
                    {r.label}
                  </span>
                  <span
                    className={`text-[11px] font-mono font-semibold ${
                      r.at_limit ? 'text-rose-700' : 'text-slate-700'
                    }`}
                  >
                    {r.used}
                    {r.limit != null ? ` / ${r.limit}` : ''}
                  </span>
                </div>
                <div className={`h-1.5 rounded-full ${tone.track} overflow-hidden`}>
                  <div
                    className={`h-full ${tone.bar} transition-[width]`}
                    style={{
                      width: r.limit
                        ? `${Math.min(100, (r.used / r.limit) * 100)}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-2.5">
            <Link
              to="/pricing"
              className={`inline-flex items-center gap-1 text-xs font-semibold ${tone.accent} hover:underline`}
              data-testid="plan-banner-cta"
            >
              See pricing <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanUsageBanner;
